import { Injectable } from '@angular/core';
import { Observable, of as obsOf, Subject } from 'rxjs';
import { Site, Service, QueueStatus, Admin, DateEvent, TimeSlot, CheckTimeSlot, Consent } from '@app-models/site';
import { AuthService } from './auth.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
// import { environment } from '../../environments/environment';
import { environment } from '../../environments/environment.prod';

import * as firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/storage';
import * as moment from 'moment';
import 'firebase/database';
import { FirebaseApp } from '@angular/fire';
import { flattenKeyArray, addKeyAsId } from '../utils';

@Injectable({
  providedIn: 'root'
})
export class SiteService {

  sites: Site[] = [
    { id: '1', displayId: '001', name: 'โรงพยาบาลพระมงกุฏเกล้า' },
    { id: '2', displayId: '002', name: 'โรงพยาบาลพญาไท' },
    { id: '3', displayId: '003', name: 'โรงพยาบาลเปาโล' }
  ];

  // Dev
  // rootAPI = 'https://us-central1-h-queue-dev.cloudfunctions.net/api-v1';

  // Production
  rootAPI = environment.rootAPI;
  headers: HttpHeaders;

  siteAdmin: Admin;
  currentSite: Site;
  currentSiteList: Site[];
  superadmins: Admin[];
  imageUpdated = new Subject<Site>();

  apiKey = environment.googleAPIKey;
  calendarId = 'th.th%23holiday%40group.v.calendar.google.com';
  googleCalendarAPI = 'https://www.googleapis.com/calendar/v3/calendars';
  holidayAPI = '';
  database: firebase.database.Database;


  constructor(
    private http: HttpClient,
    private as: AuthService) {
    this.holidayAPI = this.googleCalendarAPI + '/';
    this.holidayAPI += this.calendarId + '/events?key=' + this.apiKey;
    this.database = firebase.database();
    this.as.signoutEvent.subscribe(() => {
      this.siteAdmin = null;
      this.currentSite = null;
      this.currentSiteList = null;
      this.headers = null;
    });
  }

  async getGoogleHolidays(minYear: number, maxYear: number): Promise<DateEvent[]> {
    const promise = new Promise<DateEvent[]>((resolve, reject) => {
      this.http.get(this.holidayAPI).toPromise().then(
        (value: gapi.client.calendar.Events) => {
          const holidays: DateEvent[] = [];
          for (const holiday of value.items) {
            const start = moment(holiday.start.date);
            if (start.year() >= minYear && start.year() <= maxYear) {
              holidays.push({
                date: start,
                name: holiday.summary
              });
            }
          }
          resolve(holidays.sort(
            (a: DateEvent, b: DateEvent) => a.date.diff(b.date)));
        });
    });
    return promise;
  }

  groupDateEventByMonthYear(dates: DateEvent[]): [string[], DateEvent[][]] {
    if (dates) {
      const groups: DateEvent[][] = [];
      const keys: string[] = [];
      for (const d of dates) {
        const key = (d.date.year() + 543) + ' ' + d.date.locale('th').format('MMMM');
        if (!(key in groups)) {
          groups[key] = [];
          keys.push(key);
        }
        groups[key].push(d);
      }
      return [keys, groups];
    }
    return [[], []];

  }

  getHeaders(): Promise<HttpHeaders> {
    const promise = new Promise<HttpHeaders>(async (resolve, reject) => {
      if (this.headers) {
        resolve(this.headers);
      } else {
        await this.until(() => this.as.getTokenID());
        this.headers = new HttpHeaders({
          Authorization: 'Bearer ' + this.as.getTokenID()
        });
        return resolve(this.headers);
      }
    });
    return promise;
  }

  async getSuperAdmins(): Promise<Admin[]> {
    const promise = new Promise<Admin[]>((resolve, _) => {
      this.database.ref('/admin/superAdmins').once('value').then((snapshot) => {
        const results = snapshot.val();
        const superadmins: Admin[] = addKeyAsId<Admin>(results);
        this.superadmins = superadmins;
        resolve(this.superadmins);
      });
    });
    return promise;
  }

  async getSiteList(): Promise<Site[]> {
    const uid: string = this.as.getAuthenState().uid;
    const superadmins = await this.getSuperAdmins();
    let superAdmin = false;
    for (const key in superadmins) {
      if (key === uid) {
        superAdmin = true;
        break;
      }
    }
    const promise = new Promise<Site[]>((resolve, _) => {
      this.database.ref('/admin/sites').once('value').then((snapshot) => {
        const results = snapshot.val();
        const sites: Site[] = addKeyAsId<Site>(results);
        const permittedSites: Site[] = [];
        for (const key in sites) {
          if (Object.prototype.hasOwnProperty.call(sites, key)) {
            sites[key].admins = addKeyAsId<Admin>(sites[key].admins);
            sites[key].services = addKeyAsId<Service>(sites[key].services);
            for (const serviceKey in sites[key].services) {
              if (Object.prototype.hasOwnProperty.call(sites[key].services, serviceKey)) {
                sites[key].services[serviceKey].consents = addKeyAsId<Consent>(
                  sites[key].services[serviceKey].consents);
              }
            }
            if (superAdmin) {
              permittedSites[key] = sites[key];
              for (const adminId in superadmins) {
                if (Object.prototype.hasOwnProperty.call(superadmins, adminId)) {
                  sites[key].admins[adminId] = superadmins[adminId];
                }
              }
            } else {
              for (const adminId in sites[key].admins) {
                if (adminId === uid) {
                  permittedSites[key] = sites[key];
                }
              }
            }
          }
        }
        this.currentSiteList = permittedSites;
        resolve(permittedSites);
      });
    });
    return promise;
  }

  newSite(): Site {
    return {
      displayId: '',
      name: '',
      isEnabled: false,
      isShown: false,
      logoUrl: 'assets/images/Default-Logo.png',
      admins: [],
      services: []
    };
  }

  async createSite(site: Site): Promise<any> {
    // const headers = await this.getHeaders();
    // return this.http.post<Site>(
    //   [this.rootAPI, 'admin', 'site'].join('/'),
    //   site,
    //   { headers }
    // ).toPromise();
    return this.database.ref(['/admin', 'sites'].join('/')).push(site);
  }

  async getSite(siteId: string, local: boolean = false): Promise<Site> {
    await this.until(() => this.currentSiteList);
    const uid = this.as.getAuthenState().uid;
    if (Object.prototype.hasOwnProperty.call(this.currentSiteList, siteId)) {
      this.currentSite = this.currentSiteList[siteId];
      for (const adminId in this.currentSite.admins) {
        if (adminId === uid) {
          this.siteAdmin = this.currentSite.admins[adminId];
        }
      }
      return obsOf(this.currentSite).toPromise();
    }
  }

  async getCurrentSite(): Promise<Site> {
    await this.until(() => this.currentSite);
    return obsOf(this.currentSite).toPromise();
  }

  async updateSite(site: Site, changes: any): Promise<any> {
    return this.database.ref(['/admin', 'sites', site.id].join('/')).update(changes);
    // const headers = await this.getHeaders();
    // return this.http.put<any>(
    //   [this.rootAPI, 'admin', 'site', site.id].join('/'),
    //   site,
    //   { headers }
    // ).toPromise();
  }

  async updateServiceConsent(siteId: string, service: Service, consent: Consent): Promise<any> {
    const updateConsent: any = Object.assign({}, consent);
    updateConsent.docData = null;
    if (consent.activeDatetime) {
      updateConsent.activeDatetime = consent.activeDatetime.locale('th').format();
    }
    if (consent.expiredDatetime) {
      updateConsent.expiredDatetime = consent.expiredDatetime.locale('th').format();
    }
    return this.database.ref(
      ['/admin', 'sites', siteId, 'services', service.id, 'consents', updateConsent.id].join('/')
    ).update(updateConsent);
  }

  // async updateSiteConsent(site: Site, consent: Consent): Promise<any> {
  //   const updateConsent: any = Object.assign({}, consent);
  //   updateConsent.docData = null;
  //   if (consent.activeDatetime) {
  //     updateConsent.activeDatetime = consent.activeDatetime.locale('th').format();
  //   }
  //   if (consent.expiredDatetime) {
  //     updateConsent.expiredDatetime = consent.expiredDatetime.locale('th').format();
  //   }
  //   return this.database.ref(
  //     ['/admin', 'sites', site.id, 'consents', updateConsent.id].join('/')
  //   ).update(updateConsent);
  // }

  async uploadConsentFile(siteId: string, serviceId: string, consent: Consent): Promise<string> {
    const promise = new Promise<string>((resolve, reject) => {
      const storageRef = firebase.storage().ref(
        'consents/' + siteId + '/' + serviceId + '/' + consent.id + '.json');
      storageRef.putString(consent.docData ,  "raw" , { "contentType":"application/json" } ).then((uploadFile) => {
        storageRef.getDownloadURL().then((pathUrl) => {
          resolve(pathUrl);
        });
      });
    });
    return promise;
  }

  async getConsentContent(url: string): Promise<any> {
    const promise = new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'text';
      xhr.onload = (event) => {
        resolve(xhr.response);
      };
      xhr.open('GET', url);
      xhr.send();
    });
    return promise;
    // return this.http.get(url, httpOptions).toPromise();
  }

  async updateLocalSiteList(site: Site): Promise<boolean> {
    await this.until(() => this.currentSiteList);
    if (Object.prototype.hasOwnProperty.call(this.currentSiteList, site.id)) {
      const listedSite = this.currentSiteList[site.id];
      listedSite.name = site.name;
      listedSite.logoUrl = site.logoUrl;
      listedSite.displayId = site.displayId;
      return obsOf(true).toPromise();
    }
    return obsOf(false).toPromise();
  }

  async deleteSite(siteId: string): Promise<any> {
    // const headers = await this.getHeaders();
    // return this.http.delete<any>(
    //   [this.rootAPI, 'admin', 'site', siteId].join('/'),
    //   { headers }
    // ).toPromise();
    return this.database.ref(['/admin', 'sites', siteId].join('/')).remove();
  }

  async deleteSiteLocal(site: Site): Promise<any> {
    await this.until(() => this.currentSiteList);
    if (Object.prototype.hasOwnProperty.call(this.currentSiteList, site.id)) {
      delete this.currentSiteList[site.id];
      if (this.currentSite.id === site.id) {
        this.currentSite = null;
      }
      return obsOf(true).toPromise();
    }
    return obsOf(false).toPromise();
  }

  newService(): Service {
    return {
      displayId: '',
      name: '',
      isEnabled: true,
      isShown: false,
      checkInRadius: 2,
      queueStatuses: this.getDefaultStatusList(true)
    };
  }

  async createService(siteId: string, service: Service): Promise<any> {
    return this.database.ref(
      ['/admin/sites', siteId, 'services'].join('/')).push(service);
    // const headers = await this.getHeaders();
    // return this.http.post<Service>(
    //   [this.rootAPI, 'admin', 'site', siteId, 'service'].join('/'),
    //   service,
    //   { headers }
    // ).toPromise();
  }

  async getService(serviceId: string): Promise<Service> {
    await this.until(() => this.currentSite);
    if (this.currentSite) {
      if (Object.prototype.hasOwnProperty.call(this.currentSite.services, serviceId)) {
        return obsOf(this.currentSite.services[serviceId]).toPromise();
      }
    }
    return obsOf(this.newService()).toPromise();
  }

  async updateService(service: Service, changes: any): Promise<any> {
    // /admin/sites/{site-id}/services/{service-id}
    await this.until(() => this.currentSite);
    const siteId = this.currentSite.id;
    // console.log(siteId, service.id, changes);
    return this.database.ref(
      ['/admin/sites', siteId, 'services', service.id].join('/')
    ).update(changes);
    // await this.until(() => this.currentSite);
    // const headers = await this.getHeaders();
    // const siteId = this.currentSite.id;
    // this.http.put(
    //   [this.rootAPI, 'admin/site', siteId, 'service', service.id].join('/'),
    //   service,
    //   { headers }
    // ).toPromise();
  }

  async deleteService(serviceId: string): Promise<any> {
    // /admin/site/{site-id}/service/{service-id}
    await this.until(() => this.currentSite);
    return this.database.ref(
      ['/admin/sites', this.currentSite.id, 'services', serviceId].join('/')).remove();
    // const headers = await this.getHeaders();
    // // console.log(headers);
    // return this.http.delete(
    //   [this.rootAPI, 'admin/site', this.currentSite.id, 'service', serviceId].join('/'),
    //   { headers }
    // ).toPromise();
  }

  async deleteServiceLocal(service: Service): Promise<any> {
    await this.until(() => this.currentSite);
    if (Object.prototype.hasOwnProperty.call(this.currentSite.services, service.id)) {
      delete this.currentSite.services[service.id];
      return obsOf(true).toPromise();
    }
    return obsOf(false).toPromise();
    // const i = this.currentSite.services.indexOf(service);
    // if (i !== -1) {
    //   this.currentSite.services.splice(i, 1);
    // }
  }

  addLocalService(service: Service) {
    this.currentSite.services[service.id] = service;
  }

  getDefaultStatusList(sevenSteps: boolean): QueueStatus[] {
    const queues: QueueStatus[] = [
      { name: 'Queue_Receive', numQueuesToNotify: 5, message: 'รับบัตรคิว', shouldNotify: true, isEnabled: true },
      { name: 'SQ_Call', numQueuesToNotify: 5, message: 'รอรับเอกสาร', shouldNotify: true, isEnabled: true },
      { name: 'CheckIn', numQueuesToNotify: 5, message: 'เข้าห้องเจาะเลือด', shouldNotify: true, isEnabled: false },
      { name: 'ABS_Call', numQueuesToNotify: 5, message: 'เรียกเจาะเลือด', shouldNotify: true, isEnabled: true },
      { name: 'Pending', numQueuesToNotify: 5, message: 'คิวถูกเรียกข้าม', shouldNotify: true, isEnabled: true },
      { name: 'Close', numQueuesToNotify: 5, message: 'จบการให้บริการ', shouldNotify: false, isEnabled: true },
    ];
    if (sevenSteps) {
      queues.splice(
        2, 0, this.getDefaultQueueScanStatus());
    }
    return queues;
  }

  getDefaultQueueScanStatus(): QueueStatus {
    return { name: 'Queue_Scan', numQueuesToNotify: 5, message: 'รับเอกสาร', shouldNotify: true, isEnabled: false };
  }

  getAllTimeSlots(): TimeSlot[] {
    const slots: TimeSlot[] = [];
    for (let i = 0; i < 24; i++) {
      slots.push({
        startHour: i,
        startMin: 0,
        endHour: i,
        endMin: 30,
      });
      slots.push({
        startHour: i,
        startMin: 30,
        endHour: i + 1,
        endMin: 0,
      });
    }
    slots[47].endHour -= 1;
    slots[47].endMin = 59;
    return slots;
  }

  getSlotKey(slot: TimeSlot): string {
    const zeroPad = (num: number, places: number) => String(num).padStart(places, '0');
    return zeroPad(slot.startHour, 2) + ':' +
      zeroPad(slot.startMin, 2) + '-' +
      zeroPad(slot.endHour, 2) + ':' +
      zeroPad(slot.endMin, 2);
  }

  getAllCheckTimeSlots(): CheckTimeSlot[] {
    const slots: CheckTimeSlot[] = [];
    for (let i = 0; i < 24; i++) {
      slots.push({
        startHour: i,
        startMin: 0,
        endHour: i,
        endMin: 30,
        check: false,
      });
      slots.push({
        startHour: i,
        startMin: 30,
        endHour: i + 1,
        endMin: 0,
        check: false,
      });
    }
    slots[47].endHour -= 1;
    slots[47].endMin = 59;

    for (const slot of slots) {
      slot.key = this.getSlotKey(slot);
    }
    return slots;
  }

  compareTimeSlot(a: TimeSlot, b: TimeSlot) {
    if (a.startHour < b.startHour) { return -1; }
    if (a.startHour > b.startHour) { return 1; }
    if (a.startMin < b.startMin) { return -1; }
    if (a.startMin > b.startMin) { return 1; }
    if (a.endHour < b.endHour) { return -1; }
    if (a.endHour > b.endHour) { return 1; }
    if (a.endMin < b.endMin) { return -1; }
    if (a.endMin > b.endMin) { return 1; }
    return 0;
  }

  getTimeSlotSummary(slots: TimeSlot[], openOnWeekends: boolean): string {
    if (slots === undefined || slots.length === 0) {
      return 'ไม่มีการเปิดรับจองคิว';
    }
    const zeroPad = (num: number, places: number) => String(num).padStart(places, '0');
    slots = slots.sort(this.compareTimeSlot);
    const chunk: string[] = [];
    let prevSlot: TimeSlot = null;
    let startSlot: TimeSlot = null;
    for (const slot of slots) {
      if (prevSlot === null) {
        prevSlot = slot;
        startSlot = slot;
        continue;
      }
      if (slot.startHour !== prevSlot.endHour || slot.startMin !== prevSlot.endMin) {
        chunk.push(
          startSlot.startHour + ':' +
          zeroPad(startSlot.startMin, 2) + ' - ' +
          prevSlot.endHour + ':' +
          zeroPad(prevSlot.endMin, 2) + 'น.');
        startSlot = slot;
      }
      prevSlot = slot;
    }
    chunk.push(startSlot.startHour + ':' +
      zeroPad(startSlot.startMin, 2) + ' - ' +
      prevSlot.endHour + ':' +
      zeroPad(prevSlot.endMin, 2) + 'น.');
    if (openOnWeekends) {
      return 'ทุกวัน ' + chunk.join('; ');
    } else {
      return 'จ-ศ ' + chunk.join('; ');
    }
  }

  getAllStatusString(sevenSteps: boolean): string {
    const names: string[] = [
      'Queue_Receive',
      'SQ_Call',
      'CheckIn',
      'ABS_Call',
      'Pending',
      'Close'
    ];
    if (sevenSteps) {
      names.splice(2, 0, 'Queue_Scan');
    }
    return names.join(', ');
  }

  async getCurrentAdmin(): Promise<Admin> {
    if (this.siteAdmin) {
      return obsOf(this.siteAdmin).toPromise();
    }
    const promise = new Promise<Admin>(async (resolve, reject) => {
      const uid: string = this.as.getAuthenState().uid;
      await this.until(() => this.currentSiteList);
      // console.log('test');
      for (const siteId in this.currentSiteList) {
        if (Object.prototype.hasOwnProperty.call(this.currentSiteList, siteId)) {
          const site = this.currentSiteList[siteId];
          for (const adminId in site.admins) {
            if (adminId === uid) {
              resolve(site.admins[adminId]);
            }
          }
        }
      }
      reject('ไม่มีโรงพยาบาลที่เกี่ยวข้องกับผู้ใช้งาน กรุณาติดต่อผู้ดูแลระบบ');
    });
    return promise;
    // return this.http.get<Admin>(
    //   [this.rootAPI, 'admin/userInfo', uid].join('/'),
    //   { headers }
    // ).toPromise();
  }

  async getAdmin(adminId: string): Promise<Admin> {
    await this.until(() => this.currentSite);
    if (this.currentSite) {
      if (Object.prototype.hasOwnProperty.call(this.currentSite.admins, adminId)) {
        return obsOf(this.currentSite.admins[adminId]).toPromise();
      }
    }
    return obsOf(this.newAdmin()).toPromise();
  }

  async updateAdminInfo(siteId: string, admin: Admin, changes: any): Promise<any> {
    // if (! siteId) {
    //   const curSite = await this.getCurrentSite();
    //   siteId = curSite.id;
    // }
    if (admin.role === 'super-admin') {
      return this.database.ref(
        ['/admin', 'superAdmins', admin.id].join('/'))
        .update(changes);
    } else {
      await this.until(() => this.currentSiteList);
      const promises = [];
      for (const sId in this.currentSiteList) {
        if (Object.prototype.hasOwnProperty.call(this.currentSiteList, sId)) {
          Object.assign(this.currentSiteList[sId].admins[admin.id], changes);
          promises.push(this.database.ref(
            ['/admin', 'sites', sId, 'admins', admin.id].join('/'))
            .update(changes));
        }
      }
      return Promise.all(promises);
    }
    // const headers = await this.getHeaders();
    // return this.http.put(
    //   [this.rootAPI, 'admin/updateAdminInfo', admin.id].join('/'),
    //   admin,
    //   { headers }
    // ).toPromise();
  }

  async addAdmin(siteId: string, admin: Admin): Promise<any> {
    return this.database.ref(
      ['/admin', 'sites', siteId, 'admins', admin.id].join('/')
    ).set(admin);
  }

  // async linkSiteAdmin(siteId: string, adminId: string): Promise<any> {
  //   // admin/site/{site-id}/linkAdmin/{new-admin-id}
  //   const headers = await this.getHeaders();
  //   return this.http.put(
  //     [this.rootAPI, 'admin/site', siteId, 'linkAdmin', adminId].join('/'),
  //     null,
  //     { headers }
  //   ).toPromise();
  // }

  async deleteAdmin(siteId: string, admin: Admin): Promise<any> {
    // /admin/user/{admin-id}
    // await this.until(() => this.currentSite);
    // const headers = await this.getHeaders();
    // return this.http.delete(
    //   [this.rootAPI, 'admin/site', this.currentSite.id, 'user', adminId].join('/'),
    //   { headers }
    // ).toPromise();
    if (admin.role === 'super-admin') {
      throw new Error('ไม่สามารถลบ super-admin ได้');
    }
    return this.database.ref(
      ['/admin', 'sites', siteId, 'admins', admin.id].join('/')).remove().then(
        async (value) => {
          await this.until(() => this.currentSiteList);
          if (this.currentSiteList.length === 1) {
            const headers = await this.getHeaders();
            await this.http.delete(
              [this.rootAPI, 'admin/site', siteId, 'user', admin.id].join('/'),
              { headers }
            ).toPromise();
          }
        }
      );
  }

  async deleteAdminLocal(admin: Admin): Promise<any> {
    await this.until(() => this.currentSite);
    if (Object.prototype.hasOwnProperty.call(this.currentSite.admins, admin.id)) {
      delete this.currentSite.admins[admin.id];
    }
  }

  addLocalAdmin(admin: Admin) {
    this.currentSite.admins[admin.id] = admin;
  }

  newAdmin(): Admin {
    return {
      name: '',
      email: '',
      phoneNumber: '',
      isEnabled: true,
      isShown: false,
      role: 'admin'
    };
  }

  sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async until(fn: any) {
    while (!fn()) {
      await this.sleep(100);
    }
  }
}
